/**
 * Party Address Management Service
 * 
 * Provides address management functionality for both customers and suppliers.
 * Uses the centralized API client for authentication and error handling.
 */

import apiClient from './api';
import type { Customer } from '../types/customer';
import type { Supplier } from '../types/purchase';

export interface Address {
  id?: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  is_default?: boolean;
}

export interface AddressResponse {
  id: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export class PartyAddressService {
  /**
   * Validate address data before sending to API
   * @private
   */
  private static validateAddress(address: Address): void {
    const required: (keyof Address)[] = ['address_line1', 'city', 'state', 'pincode', 'country'];
    const missing = required.filter(field => !address[field]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }

    // Validate Indian pincode format (6 digits)
    if (!/^\d{6}$/.test(address.pincode)) {
      throw new Error('Invalid pincode format. Must be 6 digits.');
    }
  }

  /**
   * Generic request handler with error handling
   * @private
   */
  private static async request<T>(
    method: 'get' | 'post' | 'patch' | 'delete',
    url: string,
    data?: any
  ): Promise<T> {
    try {
      const response = await apiClient[method]<T>(url, data);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || `Failed to ${method} ${url}`;
      throw new Error(errorMessage);
    }
  }

  // ==================== Customer Address Management ====================

  /**
   * Update customer's billing address
   * 
   * @param customerId - UUID of the customer
   * @param address - Address data to update
   * @returns Updated customer object
   * @throws Error if update fails or validation fails
   */
  static async updateCustomerBillingAddress(
    customerId: string, 
    address: Address
  ): Promise<Customer> {
    this.validateAddress(address);
    return this.request<Customer>(
      'patch',
      `/admin/customers/${customerId}/billing-address`,
      address
    );
  }

  /**
   * Add a new shipping address for customer
   * 
   * @param customerId - UUID of the customer
   * @param address - Address data to add
   * @returns Created address object
   * @throws Error if creation fails or validation fails
   */
  static async addCustomerShippingAddress(
    customerId: string, 
    address: Address
  ): Promise<AddressResponse> {
    this.validateAddress(address);
    return this.request<AddressResponse>(
      'post',
      `/admin/customers/${customerId}/shipping-addresses`,
      address
    );
  }

  /**
   * Update an existing shipping address for customer
   * 
   * @param customerId - UUID of the customer
   * @param addressId - UUID of the address to update
   * @param address - Address data to update
   * @returns Updated address object
   * @throws Error if update fails or validation fails
   */
  static async updateCustomerShippingAddress(
    customerId: string,
    addressId: string,
    address: Address
  ): Promise<AddressResponse> {
    this.validateAddress(address);
    return this.request<AddressResponse>(
      'patch',
      `/admin/customers/${customerId}/shipping-addresses/${addressId}`,
      address
    );
  }

  /**
   * Delete a shipping address for customer
   * 
   * @param customerId - UUID of the customer
   * @param addressId - UUID of the address to delete
   * @returns Success response
   * @throws Error if deletion fails
   */
  static async deleteCustomerShippingAddress(
    customerId: string, 
    addressId: string
  ): Promise<{ success: boolean; message: string }> {
    return this.request(
      'delete',
      `/admin/customers/${customerId}/shipping-addresses/${addressId}`
    );
  }

  /**
   * Set a shipping address as default for customer
   * 
   * @param customerId - UUID of the customer
   * @param addressId - UUID of the address to set as default
   * @returns Updated address object
   * @throws Error if update fails
   */
  static async setDefaultShippingAddress(
    customerId: string, 
    addressId: string
  ): Promise<AddressResponse> {
    return this.request<AddressResponse>(
      'patch',
      `/admin/customers/${customerId}/shipping-addresses/${addressId}/set-default`
    );
  }

  // ==================== Supplier Address Management ====================

  /**
   * Update supplier's address
   * 
   * @param supplierId - UUID of the supplier
   * @param address - Address data to update
   * @returns Updated supplier object
   * @throws Error if update fails or validation fails
   */
  static async updateSupplierAddress(
    supplierId: string, 
    address: Address
  ): Promise<Supplier> {
    this.validateAddress(address);
    return this.request<Supplier>(
      'patch',
      `/admin/suppliers/${supplierId}/address`,
      address
    );
  }
}

export default PartyAddressService;
